import {
  ActionIcon,
  Button,
  FileButton,
  Group,
  Image,
  Loader,
  ScrollArea,
  SegmentedControl,
  Text,
  Tooltip,
  VisuallyHidden,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  Eye,
  EyeClosed,
  Folder,
  Image as ImageIcon,
  LayoutGrid,
  RotateCcw,
  Upload,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";

import { useGetAsset, useRestoreAsset } from "../../api/generated/endpoints/assets/assets";
import { MediaMime } from "../../api/generated/zod";
import type { Asset, AssetHit } from "../../api/generated/model";
import { refreshAccessScope } from "../../api/session";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { CreatorFilter } from "../../platform/media/CreatorFilter";
import { useSemanticSearchFlag } from "../../platform/features";
import { ASSET_SEARCH_MAX, hitAssets, hitKey, useAssetSearchPages } from "../../platform/media/asset-search";
import { LIBRARY_SEARCH_MAX, useLibraryPages } from "../../platform/media/library-pages";
import { useAssetDelete } from "../../platform/media/asset-delete";
import { shapeRatio } from "../../platform/media/asset-shape";
import { AssetMenu } from "../../platform/media/AssetMenu";
import { LibraryPreview } from "../../platform/media/LibraryPreview";
import { useRefuseStrayFileDrops } from "../../platform/media/stray-drops";
import { useMembership } from "../../platform/membership";
import { useTrashStay } from "../../platform/media/trash-days";
import { canEditContent, editsAnyContent } from "../../platform/permissions";
import { readPref, writePref } from "../../platform/prefs";
import { useAddUploads } from "../../platform/uploads/UploadsProvider";
import dropOnDark from "../../ui/assets/drag-and-drop-black.svg";
import dropOnLight from "../../ui/assets/drag-and-drop-white.svg";
import { EmptyState } from "../../ui/EmptyState";
import { LoadAhead, type MorePages } from "../../ui/LoadAhead";
import { PageColumns } from "../../ui/PageColumns";
import { columnMark } from "../../ui/DisplaySettings";
import { VirtualMasonry, type GridCardProps } from "../../ui/VirtualMasonry";
import { VirtualRows } from "../../ui/VirtualRows";
import { SubmitSearch } from "../../ui/SubmitSearch";
import { AssetCard } from "./AssetCard";
import { useBulkActions } from "./bulk-actions";
import { cardDragOf, isCardDrag, startCardDrag, type CardDrag } from "./card-drag";
import { EmptyTrash } from "./EmptyTrash";
import { BulkBar } from "./BulkBar";
import { FavoritesToggle, FolderPanel } from "./FolderPanel";
import { FoldersSection } from "./FoldersSection";
import classes from "./library.module.css";
import type { LibrarySearch } from "./library-search";
import { useIndexingPoll, useRefreshLibrary } from "./library-query";
import { LibraryToolbar } from "./LibraryToolbar";
import { ProjectsLayout } from "./ProjectsLayout";
import { SavingCard } from "./SavingCard";
import { useSelection } from "./selection";
import { UploadAs } from "./UploadAs";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/library";
/** Live's page of the library (`LIBRARY_ASSETS_LIMIT`). */
const LIBRARY_PAGE = 48;

/**
 * The brand's library (S1): ready files, newest first, filtered by type and folder, with the
 * trash beside it. The filter lives in the URL, so a view can be shared and survives a reload.
 * A job's "Save to library" links here with `?saving=`, shown as saving until it is ready.
 */
export function LibraryScreen() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/library" });
  const onSearch = useCallback(
    (update: (current: LibrarySearch) => LibrarySearch) => navigate({ search: update, replace: true }),
    [navigate],
  );
  return <LibraryView teamId={teamId} brandId={brandId} search={search} onSearch={onSearch} />;
}

export interface LibraryViewProps {
  teamId: string;
  brandId: string;
  /** The view, as the page's URL holds it (`library-search.ts`). */
  search: LibrarySearch;
  /** Changes the view in the page's URL, replacing the entry. */
  onSearch: (update: (current: LibrarySearch) => LibrarySearch) => Promise<void>;
}

/**
 * The library's content on any page whose URL holds its view: the Library page's own, and Pro's
 * (spec ui-perf/ui-switch-classic-simple-pro, PR e3, composed in `app/`).
 */
export function LibraryView({ teamId, brandId, search, onSearch }: LibraryViewProps) {
  const queryClient = useQueryClient();
  const me = useMembership(teamId);
  const trash = search.trash === true;
  // The trash's days, the API's own (`TrashSummary.days`), for the empty trash's words.
  const stay = useTrashStay(teamId, brandId, trash);
  const filter = {
    kind: search.kind,
    // Live's project scope (#445): "All assets" lists the brand while the folder stays chosen.
    folderId: search.scope === "all" ? undefined : search.folder,
    recursive: search.nested === true && search.q === undefined,
    trash,
    search: search.q,
    source: search.source === undefined ? undefined : [search.source],
    createdBy: search.by,
    productId: search.product,
    order: search.order,
  };
  // Live's search is semantic (spec S3): words find what the files show, best first, a video once
  // for each scene that matches. Behind its flag; off, the words match names, as before.
  const flag = useSemanticSearchFlag();
  const semanticOn = flag === true;
  const semantic = semanticOn && search.q !== undefined && !trash;
  // Words in the link wait for the flag: the name search is not read first and thrown away when
  // the semantic one takes over (spec S3, decision 8).
  const waiting = flag === undefined && search.q !== undefined && !trash;
  const library = useLibraryPages(teamId, brandId, filter, !semantic && !waiting, { size: LIBRARY_PAGE });
  const found = useAssetSearchPages(teamId, brandId, search.q ?? "", filter, semantic);
  // The same arrays while the pages are: the grid places its cards again only when they change.
  const hits = useMemo(() => (semantic ? (found.data?.pages.flatMap((page) => page.items) ?? []) : []), [semantic, found.data]);
  const shown = semantic ? found : library;
  // Waiting for the flag is loading too: the name read is held, so its own state is idle.
  const loading = waiting || (shown.isPending && shown.fetchStatus !== "idle");
  const refresh = useRefreshLibrary(teamId, brandId);
  // After a refusal the scope is read again too: the hint that offered the button may be stale.
  const refused = () => Promise.all([refresh(), refreshAccessScope(queryClient)]);
  const deletion = useAssetDelete(teamId, brandId);
  // The file the full-screen preview shows (decision 21), by id; null while it is closed.
  // A copied link (live's "Copy link") names its file: the preview opens on it, read on its own
  // when the list does not hold it (another folder, a filter, a later page).
  const [previewing, setPreviewing] = useState<string | null>(search.preview ?? null);
  // Restoring is not destructive, so it needs no confirmation.
  const restore = useRestoreAsset({ mutation: { onSuccess: refresh, onError: refused } });
  const setSearch = (patch: Partial<typeof search>) =>
    void onSearch((current) => ({ ...current, ...patch }));
  // The saved file is ready: the lists hold it now, so they are read again and the URL lets go.
  const saved = useCallback(async () => {
    await refresh();
    await onSearch((current) => ({ ...current, saving: undefined }));
  }, [refresh, onSearch]);

  // Live's panel starts closed and keeps no memory of it; its star shows the favourites alone.
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [favorites, setFavorites] = useState(false);
  // The assets' own scroll, which the next page watches for its end (issue 784).
  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  // Another folder starts in its own scope, flat, as live resets both.
  const chooseFolder = (folder: string | undefined) =>
    setSearch({ folder, scope: undefined, nested: undefined });
  // The app's upload queue (spec upload-tray): its tray stays as the user leaves the library.
  const addUploads = useAddUploads();
  // Live's active project (spec upload-into-project): "Upload media" and drops land in the open
  // folder, whatever the scope toggle shows.
  const here = { teamId, brandId, folderId: search.folder ?? null };
  const [dragging, setDragging] = useState(false);
  // Clears the chooser after each pick, so the same file can be chosen again ("_1" naming).
  const resetChooser = useRef<() => void>(null);
  // A card dragged onto a project tile is no upload, even where its picture comes along as a file.
  const carriesFiles = (event: DragEvent) =>
    event.dataTransfer.types.includes("Files") && !isCardDrag(event);
  // While the library is open, a file dropped on the header, the sidebar or the trash does nothing.
  useRefuseStrayFileDrops();
  // As live: files dropped anywhere on the library page upload, like "Upload media" (not in the
  // trash); the frame shows over the gallery.
  const drop = trash
    ? {}
    : {
        onDragEnter: (event: DragEvent) => {
          if (carriesFiles(event)) setDragging(true);
        },
        onDragOver: (event: DragEvent) => {
          if (!carriesFiles(event)) return;
          event.preventDefault();
          setDragging(true);
        },
        onDragLeave: (event: DragEvent) => {
          const next = event.relatedTarget;
          if (!(next instanceof Node && event.currentTarget.contains(next))) setDragging(false);
        },
        onDrop: (event: DragEvent) => {
          if (!carriesFiles(event)) return;
          event.preventDefault();
          setDragging(false);
          addUploads(Array.from(event.dataTransfer.files), here);
        },
      };
  // Live's display settings (spec library-rows): a count kept from the old slider shows at its mark.
  const [columns, setColumns] = useState(() => columnMark(readPref("libraryColumns")));
  const [layout, setLayout] = useState(() => readPref("libraryLayout"));
  const [rowHeight, setRowHeight] = useState(() => readPref("libraryRowHeight"));
  const assets = useMemo(
    () => (semantic ? hitAssets(hits) : (library.data?.pages.flatMap((page) => page.items) ?? [])),
    [semantic, hits, library.data],
  );
  // A file being indexed for search (spec S3) is read again every 10 s, alone, so its card's
  // state changes as the worker answers.
  useIndexingPoll(
    teamId,
    brandId,
    semanticOn && !semantic && !trash
      ? assets.filter((asset) => asset.index_status === "pending").map((asset) => asset.id)
      : [],
  );
  const listed = assets.some((asset) => asset.id === search.preview);
  const linked = useGetAsset(teamId, brandId, search.preview ?? "", {
    query: { enabled: search.preview !== undefined && !shown.isPending && !listed },
  });
  const linkedAsset = !listed && linked.data !== undefined && linked.data.deleted_at === null ? linked.data : undefined;
  // The link's file, alone, while the list does not hold it.
  const more: MorePages = {
    has: shown.hasNextPage,
    loading: shown.isFetchingNextPage,
    load: () => void shown.fetchNextPage(),
  };
  const shownAssets = linkedAsset !== undefined && previewing === linkedAsset.id ? [linkedAsset] : assets;
  // Until the list or the file itself holds the link's file, the preview waits rather than show
  // another; a file that is gone (deleted, or never this brand's) opens nothing.
  const linkMissing =
    search.preview !== undefined &&
    previewing === search.preview &&
    !shownAssets.some((asset) => asset.id === search.preview);
  // Live's selection and bulk bar (S1c): one view of the list at a time, never in the trash.
  const view = [search.folder, search.scope, search.nested, search.kind, search.q, search.source];
  const viewKey = JSON.stringify([...view, search.by, search.product, search.order, trash]);
  const selection = useSelection(viewKey);
  const selected = assets.filter((asset) => selection.ids.has(asset.id));
  const bulk = useBulkActions(teamId, brandId, selected, selection.clear, viewKey);
  const mayChange = useCallback(
    (asset: Asset) => me !== undefined && canEditContent(me.role, asset.created_by ?? "", me.userId),
    [me],
  );
  const saving = search.saving !== undefined && !trash;
  const savingId = saving ? search.saving : undefined;
  // The grid's cards: the file being saved first, then the files (a search's hits, each a scene),
  // each the same object while its file is (`cardOf`).
  const gridItems = useMemo<GridItem[]>(
    () => [...(savingId === undefined ? [] : [{ saving: savingId }]), ...(semantic ? hits : assets.map(cardOf))],
    [savingId, semantic, hits, assets],
  );
  // What the cards call, read when they call it: the cards keep one `data` while what they show
  // stays, so a scroll step or a new page renders no card again (issue 794).
  const calls = useRef<CardCalls | null>(null);
  // A card's drag under way, which the project tiles read as it passes (spec library-card-drag).
  const cardDrag = useRef<CardDrag | null>(null);
  useEffect(() => {
    calls.current = {
      open: setPreviewing,
      toggle: selection.toggle,
      ask: deletion.ask,
      restore: (asset) => restore.mutate({ teamId, brandId, assetId: asset.id }),
      saved: () => void saved(),
      // As live's: a chosen card drags every chosen file, another card its own.
      drag: (event, asset) => {
        const drag = cardDragOf(selection.ids.has(asset.id) ? selected : [asset]);
        startCardDrag(event, drag);
        cardDrag.current = drag;
      },
      dragEnd: () => {
        cardDrag.current = null;
      },
    };
  }, [selection.toggle, selection.ids, selected, deletion.ask, restore, teamId, brandId, saved]);
  const restoring = restore.isPending ? restore.variables.assetId : null;
  const cardData = useMemo<CardData>(
    () => ({
      teamId,
      brandId,
      trash,
      indexed: semanticOn && !trash,
      selected: selection.ids,
      restoring,
      mayChange,
      calls,
      rows: layout === "rows",
    }),
    [teamId, brandId, trash, semanticOn, selection.ids, restoring, mayChange, layout],
  );
  const empty = shown.isSuccess && assets.length === 0 && !saving;
  // A search or a filter that finds nothing says live's words for it, not "no files yet".
  const narrowed = [search.q, search.source, search.by, search.product, search.kind].some(
    (each) => each !== undefined,
  );

  const top = (
    <>
      <Group justify="space-between" gap={8} wrap="wrap" className={classes.rowOne}>
        <Group gap="sm" wrap="nowrap">
          <SubmitSearch
            label="Search media files"
            value={search.q ?? ""}
            maxLength={semanticOn ? ASSET_SEARCH_MAX : LIBRARY_SEARCH_MAX}
            onSearch={(q) => setSearch({ q: q === "" ? undefined : q })}
          />
          <CreatorFilter
            teamId={teamId}
            value={search.by ?? []}
            onChange={(by) => setSearch({ by: by.length === 0 ? undefined : by })}
          />
        </Group>
        <Group gap={8} wrap="nowrap">
          {/* Live's ProjectScopeToggle (issue 445): off on "All assets". */}
          <Tooltip label={search.scope === "all" ? "All assets" : "Current project"}>
            <ActionIcon
              variant="subtle"
              aria-label={search.scope === "all" ? "All assets" : "Current project"}
              aria-pressed={search.scope !== "all" && search.folder !== undefined}
              disabled={search.folder === undefined}
              onClick={() => setSearch({ scope: search.scope === "all" ? undefined : "all" })}
            >
              {search.scope === "all" ? (
                <LayoutGrid size={20} aria-hidden />
              ) : (
                <Folder size={20} aria-hidden />
              )}
            </ActionIcon>
          </Tooltip>
          {/* Live's RecursiveToggle: off on "All assets" and while a search runs. */}
          <Tooltip label={search.nested === true ? "Collapse nested" : "Show nested"}>
            <ActionIcon
              variant="subtle"
              aria-label={search.nested === true ? "Collapse nested" : "Show nested"}
              aria-pressed={search.nested === true}
              disabled={search.folder === undefined || search.q !== undefined}
              onClick={() => setSearch({ nested: search.nested === true ? undefined : true })}
            >
              {search.nested === true ? (
                <EyeClosed size={20} aria-hidden />
              ) : (
                <Eye size={20} aria-hidden />
              )}
            </ActionIcon>
          </Tooltip>
          {/* Live's view switch (issue 446): the files, or the folder's subprojects as cards too. */}
          <SegmentedControl
            aria-label="View"
            value={search.view ?? "assets"}
            onChange={(value) => setSearch({ view: value === "folders" ? "folders" : undefined })}
            data={[
              {
                value: "assets",
                label: (
                  <>
                    <ImageIcon size={16} aria-hidden />
                    <VisuallyHidden>Assets</VisuallyHidden>
                  </>
                ),
              },
              {
                value: "folders",
                label: (
                  <>
                    <Folder size={16} aria-hidden />
                    <VisuallyHidden>Folders</VisuallyHidden>
                  </>
                ),
              },
            ]}
          />
          {/* As live: the button opens the file chooser at once; the box shows the queue. */}
          <FileButton
            multiple
            accept={MediaMime.options.join(",")}
            resetRef={resetChooser}
            onChange={(files) => {
              resetChooser.current?.();
              addUploads(files, here);
            }}
            inputProps={{ "aria-label": "Choose files to upload" }}
          >
            {(props) => (
              <Button {...props} variant="outline" leftSection={<ImageIcon size={18} aria-hidden />}>
                Upload media
              </Button>
            )}
          </FileButton>
          <UploadAs
            teamId={teamId}
            brandId={brandId}
            onFiles={(files, folderId) => addUploads(files, { teamId, brandId, folderId })}
          />
        </Group>
      </Group>
    </>
  );

  return (
    <div className={classes.screen} {...drop}>
      <PageColumns top={top} scrollable={false}>
        <div className={classes.body}>
          <ProjectsLayout
            open={foldersOpen}
            onToggle={() => setFoldersOpen(!foldersOpen)}
            headerSlot={<FavoritesToggle on={favorites} onChange={setFavorites} />}
            header={
              <LibraryToolbar
                teamId={teamId}
                view={{
                  kind: search.kind,
                  source: search.source,
                  product: search.product,
                  order: search.order ?? "newest",
                  trash,
                }}
                onView={(patch) =>
                  setSearch({
                    ...("kind" in patch ? { kind: patch.kind } : {}),
                    ...("source" in patch ? { source: patch.source } : {}),
                    ...("product" in patch ? { product: patch.product } : {}),
                    ...("order" in patch ? { order: patch.order === "oldest" ? "oldest" : undefined } : {}),
                    ...("trash" in patch ? { trash: patch.trash === true ? true : undefined } : {}),
                  })
                }
                columns={columns}
                onColumns={(next) => {
                  setColumns(next);
                  writePref("libraryColumns", next);
                }}
                layout={layout}
                onLayout={(next) => {
                  setLayout(next);
                  writePref("libraryLayout", next);
                }}
                rowHeight={rowHeight}
                onRowHeight={(next) => {
                  const height = next === 150 || next === 250 ? next : 200;
                  setRowHeight(height);
                  writePref("libraryRowHeight", height);
                }}
              />
            }
            panel={
              <FolderPanel
                teamId={teamId}
                brandId={brandId}
                folderId={search.folder}
                onChoose={chooseFolder}
                favorites={favorites}
                onFavorites={setFavorites}
              />
            }
          >
            {dragging ? (
              <div className={classes.dropZone} aria-hidden>
                <span className={classes.dropIcon}>
                  <Upload size={22} />
                </span>
                <Text size="sm" c="var(--cai-color-text-muted)">
                  Drop file(s) to start uploading
                </Text>
              </div>
            ) : null}
            <ScrollArea type="auto" offsetScrollbars className={classes.gallery} viewportRef={setViewport}>
              {search.view === "folders" && !trash ? (
                <>
                  <FoldersSection
                    teamId={teamId}
                    brandId={brandId}
                    folderId={search.folder}
                    onChoose={chooseFolder}
                    dragging={cardDrag}
                    onMoved={selection.clear}
                  />
                  <Text fw={600} size="md" className={classes.assetsHead}>
                    Assets
                  </Text>
                </>
              ) : null}
              {trash && me !== undefined && editsAnyContent(me.role) ? (
                <EmptyTrash teamId={teamId} brandId={brandId} />
              ) : null}
              {restore.isError ? <ErrorAlert error={restore.error} /> : null}
              {shown.isError ? <ErrorAlert error={shown.error} /> : null}
              {bulk.note === null ? null : (
                <Text size="sm" role="alert">
                  {bulk.note}
                </Text>
              )}
              {loading ? (
                <Group justify="center" py="xl">
                  <Loader size="sm" aria-label="Loading the library" />
                </Group>
              ) : null}
              {empty ? (
                <EmptyState
                  image={
                    <>
                      <Image src={dropOnLight} alt="" w={250} darkHidden />
                      <Image src={dropOnDark} alt="" w={250} lightHidden />
                    </>
                  }
                  title={
                    narrowed ? "No results found" : trash ? "The trash is empty." : "No files here yet."
                  }
                  description={
                    narrowed
                      ? "Check for typos or filters, or try a different search query."
                      : trash
                        ? `Deleted files stay ${stay}.`
                        : "Upload media, or save a generation to this brand's library."
                  }
                />
              ) : null}
              {layout === "rows" ? (
                <VirtualRows
                  items={gridItems}
                  keyOf={cardKey}
                  ratioOf={cardRatio}
                  card={LibraryCard}
                  data={cardData}
                  height={rowHeight}
                  root={viewport}
                  label="Library"
                />
              ) : (
                <VirtualMasonry
                  items={gridItems}
                  keyOf={cardKey}
                  ratioOf={cardRatio}
                  card={LibraryCard}
                  data={cardData}
                  columns={columns}
                  root={viewport}
                  label="Library"
                />
              )}
              {assets.length > 0 ? (
                <LoadAhead more={more} loaded={assets.length} root={viewport} ahead={3} label="Loading more files" />
              ) : null}
            </ScrollArea>
          </ProjectsLayout>
        </div>
        <LibraryPreview
          teamId={teamId}
          brandId={brandId}
          assets={shownAssets}
          // A search's preview steps through its hits, a video's from its scene, as live's.
          hits={semantic && shownAssets === assets ? hits : undefined}
          current={linkMissing ? null : previewing}
          onShow={(id) => {
            setPreviewing(id);
            // Closed, the link has done its work.
            if (id === null && search.preview !== undefined) setSearch({ preview: undefined });
          }}
          more={more}
          trash={trash}
          mayChange={mayChange}
          onDelete={deletion.ask}
          onRestore={(asset) => restore.mutate({ teamId, brandId, assetId: asset.id })}
          restoring={restore.isPending ? restore.variables.assetId : null}
          paused={deletion.asking}
        />
        {deletion.dialog}
        {bulk.dialogs}
        <BulkBar
          count={selected.length}
          onClear={selection.clear}
          {...bulk.bar}
          onReindex={semanticOn ? bulk.bar.onReindex : undefined}
        />
      </PageColumns>
    </div>
  );
}

/** A card of the library's grid: the file being saved, or a file (a search's hit, at its scene). */
type GridItem = { saving: string } | Pick<AssetHit, "asset" | "scene">;

/** What the cards call (`LibraryScreen`'s, as it is when they call). */
interface CardCalls {
  open: (key: string) => void;
  toggle: (id: string) => void;
  ask: (asset: Asset) => void;
  restore: (asset: Asset) => void;
  saved: () => void;
  drag: (event: DragEvent, asset: Asset) => void;
  dragEnd: () => void;
}

/** What every card of the grid reads besides its file (`VirtualMasonry`'s `data`). */
interface CardData {
  teamId: string;
  brandId: string;
  trash: boolean;
  /** Semantic search is on: a card says when its file is being indexed or failed (spec S3). */
  indexed: boolean;
  selected: ReadonlySet<string>;
  /** The file whose Restore is under way, or none. */
  restoring: string | null;
  mayChange: (asset: Asset) => boolean;
  calls: { readonly current: CardCalls | null };
  /** Live's rows layout (spec library-rows): each card fills its cell. */
  rows: boolean;
}

const CARDS = new WeakMap<Asset, GridItem>();

/** A file's card item, the same object for as long as the list holds the same file. */
function cardOf(asset: Asset): GridItem {
  let card = CARDS.get(asset);
  if (card === undefined) {
    card = { asset, scene: null };
    CARDS.set(asset, card);
  }
  return card;
}

const cardKey = (item: GridItem) => ("saving" in item ? `saving:${item.saving}` : hitKey(item));
const cardRatio = (item: GridItem) => ("saving" in item ? 1 : shapeRatio(item.asset));

/** One card of the grid: the file being saved, or a file with its menu, its check and its open. */
function LibraryCard({ item, data }: GridCardProps<GridItem, CardData>) {
  if ("saving" in item) {
    return (
      <SavingCard teamId={data.teamId} brandId={data.brandId} assetId={item.saving} onReady={() => data.calls.current?.saved()} />
    );
  }
  const { asset } = item;
  // A file opens where the lightbox has something to show: the file, or, in the trash, its still
  // (issue 828, bug 30); a pending or failed file stays a plain box.
  const { original, preview, poster, thumb } = asset.urls;
  const viewable = (original ?? preview ?? poster ?? thumb ?? null) !== null;
  return (
    <AssetCard
      asset={asset}
      scene={item.scene}
      indexed={data.indexed}
      actions={<CardActions asset={asset} data={data} />}
      onOpen={viewable ? () => data.calls.current?.open(hitKey(item)) : undefined}
      fill={data.rows}
      drag={
        data.trash
          ? undefined
          : {
              onDragStart: (event) => data.calls.current?.drag(event, asset),
              onDragEnd: () => data.calls.current?.dragEnd(),
            }
      }
      selection={
        data.trash
          ? undefined
          : { checked: data.selected.has(asset.id), onToggle: () => data.calls.current?.toggle(asset.id) }
      }
    />
  );
}

/** Live's card: its "..." menu alone (S1 decision 22); the trash keeps its Restore (ours). */
function CardActions({ asset, data }: { asset: Asset; data: CardData }) {
  if (!data.trash) {
    return (
      <AssetMenu
        teamId={data.teamId}
        brandId={data.brandId}
        asset={asset}
        mayChange={data.mayChange(asset)}
        onDelete={(file) => data.calls.current?.ask(file)}
      />
    );
  }
  return (
    <Group gap={4} wrap="nowrap">
      {!data.mayChange(asset) ? null : (
        <Tooltip label="Restore">
          <ActionIcon
            variant="text-white"
            aria-label={`Restore ${asset.name}`}
            loading={data.restoring === asset.id}
            onClick={() => data.calls.current?.restore(asset)}
          >
            <RotateCcw size={20} aria-hidden />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
}
